/**
 * Password-protected zip, in the format 7-Zip, Keka, WinZip and p7zip call
 * "AES-256" — WinZip's AE-2 spec.
 *
 * Not the zip password everyone's grandmother's tools support: legacy
 * ZipCrypto is broken and would be theatre over a file holding every
 * transaction you have. AE-2 is real encryption, at the cost of needing a
 * real archiver to open it (macOS's Archive Utility and stock `unzip` cannot).
 *
 * Per entry: PBKDF2-HMAC-SHA1 over the password (1000 rounds) gives a 256-bit
 * encryption key, a 256-bit authentication key and a two-byte verifier; the
 * deflated bytes go through AES-CTR; an HMAC-SHA1 of the ciphertext, truncated
 * to 10 bytes, closes the entry.
 */
// Explicit extension: the browser test opens a real backup from plain Node,
// where extensionless imports do not resolve (as in db/migrate.js).
import { ctrLittleEndian } from './aes.js'

const ROUNDS = 1000
const SALT_BYTES = 16 // AES-256
const MAC_BYTES = 10
const AES_METHOD = 99
const DEFLATED = 8

const utf8 = (text) => new TextEncoder().encode(text)

const u16 = (b, o) => b[o] | (b[o + 1] << 8)
const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0

export const looksLikeZip = (bytes) => bytes[0] === 0x50 && bytes[1] === 0x4b

async function deriveKeys(password, salt) {
  const base = await crypto.subtle.importKey('raw', utf8(password), 'PBKDF2', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-1', salt, iterations: ROUNDS },
    base,
    (32 + 32 + 2) * 8,
  )
  const all = new Uint8Array(bits)
  return { encKey: all.slice(0, 32), macKey: all.slice(32, 64), verifier: all.slice(64, 66) }
}

async function authenticate(macKey, ciphertext) {
  const key = await crypto.subtle.importKey(
    'raw',
    macKey,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  )
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, ciphertext))
  return mac.slice(0, MAC_BYTES)
}

async function deflate(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

async function inflate(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** The 0x9901 extra field that marks an entry as AE-2, AES-256, deflated. */
function aesExtraField() {
  const field = new Uint8Array(11)
  const view = new DataView(field.buffer)
  view.setUint16(0, 0x9901, true) // header id
  view.setUint16(2, 7, true) // data size
  view.setUint16(4, 2, true) // AE-2
  field[6] = 0x41 // 'A'
  field[7] = 0x45 // 'E'
  field[8] = 0x03 // AES-256
  view.setUint16(9, DEFLATED, true) // what the data really is, under the encryption
  return field
}

const concat = (chunks) => {
  const out = new Uint8Array(chunks.reduce((sum, c) => sum + c.length, 0))
  let at = 0
  for (const chunk of chunks) {
    out.set(chunk, at)
    at += chunk.length
  }
  return out
}

/**
 * @param {{name: string, bytes: Uint8Array}[]} files
 * @param {string} password
 * @returns {Promise<Uint8Array>} the archive
 */
export async function zipWithPassword(files, password) {
  if (!password) throw Object.assign(new Error('A password is needed to protect the backup'), { code: 'zip.need-password' })
  const local = []
  const central = []
  let offset = 0

  for (const file of files) {
    const name = utf8(file.name)
    const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES))
    const { encKey, macKey, verifier } = await deriveKeys(password, salt)
    const deflated = await deflate(file.bytes)
    const ciphertext = ctrLittleEndian(encKey, deflated)
    const mac = await authenticate(macKey, ciphertext)
    const payload = concat([salt, verifier, ciphertext, mac])

    const header = new Uint8Array(30)
    const hv = new DataView(header.buffer)
    hv.setUint32(0, 0x04034b50, true)
    hv.setUint16(4, 51, true) // version needed for AES
    hv.setUint16(6, 0x0001, true) // encrypted
    hv.setUint16(8, AES_METHOD, true)
    hv.setUint32(14, 0, true) // AE-2 stores no CRC
    hv.setUint32(18, payload.length, true)
    hv.setUint32(22, file.bytes.length, true)
    hv.setUint16(26, name.length, true)
    hv.setUint16(28, 11, true) // the extra field below

    const entryAt = offset
    local.push(header, name, aesExtraField(), payload)
    offset += 30 + name.length + 11 + payload.length

    const record = new Uint8Array(46)
    const rv = new DataView(record.buffer)
    rv.setUint32(0, 0x02014b50, true)
    rv.setUint16(4, 51, true)
    rv.setUint16(6, 51, true)
    rv.setUint16(8, 0x0001, true)
    rv.setUint16(10, AES_METHOD, true)
    rv.setUint32(16, 0, true)
    rv.setUint32(20, payload.length, true)
    rv.setUint32(24, file.bytes.length, true)
    rv.setUint16(28, name.length, true)
    rv.setUint16(30, 11, true)
    rv.setUint32(42, entryAt, true)
    central.push(record, name, aesExtraField())
  }

  const directoryAt = offset
  const directory = central.reduce((sum, part) => sum + part.length, 0)
  const end = new Uint8Array(22)
  const ev = new DataView(end.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(8, files.length, true)
  ev.setUint16(10, files.length, true)
  ev.setUint32(12, directory, true)
  ev.setUint32(16, directoryAt, true)

  return concat([...local, ...central, end])
}

/**
 * @param {Uint8Array} archive
 * @param {string} password
 * @returns {Promise<{name: string, bytes: Uint8Array}[]>}
 */
export async function unzipWithPassword(archive, password) {
  const bytes = archive instanceof Uint8Array ? archive : new Uint8Array(archive)
  if (!looksLikeZip(bytes)) throw Object.assign(new Error('That is not a zip file'), { code: 'zip.not-zip' })

  const files = []
  let at = 0
  while (at + 30 <= bytes.length && u32(bytes, at) === 0x04034b50) {
    const method = u16(bytes, at + 8)
    const compressed = u32(bytes, at + 18)
    const nameLength = u16(bytes, at + 26)
    const extraLength = u16(bytes, at + 28)
    const name = new TextDecoder().decode(bytes.subarray(at + 30, at + 30 + nameLength))
    const dataAt = at + 30 + nameLength + extraLength
    const payload = bytes.subarray(dataAt, dataAt + compressed)

    if (method !== AES_METHOD) {
      throw Object.assign(new Error('That zip is not password-protected in the way OKANE writes them'), { code: 'zip.not-ours' })
    }
    const salt = payload.subarray(0, SALT_BYTES)
    const given = payload.subarray(SALT_BYTES, SALT_BYTES + 2)
    const ciphertext = payload.subarray(SALT_BYTES + 2, payload.length - MAC_BYTES)
    const mac = payload.subarray(payload.length - MAC_BYTES)

    const { encKey, macKey, verifier } = await deriveKeys(password, salt)
    if (given[0] !== verifier[0] || given[1] !== verifier[1]) {
      throw Object.assign(new Error('Wrong password'), { code: 'zip.wrong-password' })
    }
    const expected = await authenticate(macKey, ciphertext)
    if (expected.some((byte, i) => byte !== mac[i])) {
      throw Object.assign(new Error('That backup has been damaged since it was made'), { code: 'zip.damaged' })
    }
    const deflated = ctrLittleEndian(encKey, ciphertext)
    files.push({ name, bytes: await inflate(deflated) })
    at = dataAt + compressed
  }

  if (!files.length) throw Object.assign(new Error('There is nothing in that zip'), { code: 'zip.empty' })
  return files
}
